import React, { useRef, useEffect, useState, useCallback } from 'react';
import ChatMessage from './components/ChatMessage';
import WelcomeScreen from './components/WelcomeScreen';
import TypingIndicator from './components/TypingIndicator';
import SourcePanel from './components/SourcePanel';
import { useWebSocket } from './hooks/useWebSocket';
import { LOGOS, INPUT_PLACEHOLDER, DISCLAIMER } from './config';
import './App.css';
import Avatar from './components/Avatar';

function SendIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  );
}

export default function App() {
  const { status, messages, isTyping, sendMessage, clearMessages, STATUS, welcomemessages, chat_widget_icon, data_assistant_icon } = useWebSocket();
  const [text, setText] = useState('');
  const [sourcePanel, setSourcePanel] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 160) + 'px';
  }, [text]);

  const handleOpenSourceFile = useCallback((files, index) => {
    setSourcePanel({ files, activeIndex: index });
  }, []);

  const handleCloseSourcePanel = useCallback(() => {
    setSourcePanel(null);
  }, []);

  const handleChangeSourceFile = useCallback((index) => {
    setSourcePanel((prev) => prev ? { ...prev, activeIndex: index } : prev);
  }, []);

  const handleSend = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed || isTyping) return;
    sendMessage(trimmed);
    setText('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  }, [text, isTyping, sendMessage]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const statusColor = {
    [STATUS.CONNECTED]: '#22c55e',
    [STATUS.CONNECTING]: '#f59e0b',
    [STATUS.DISCONNECTED]: '#d1d5db',
    [STATUS.ERROR]: '#ef4444',
  }[status] || '#d1d5db';

  const statusLabel = {
    [STATUS.CONNECTED]: 'Connected',
    [STATUS.CONNECTING]: 'Connecting…',
    [STATUS.DISCONNECTED]: 'Disconnected',
    [STATUS.ERROR]: 'Connection error',
  }[status] || '';

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-logo">
          <Avatar
            src={chat_widget_icon || data_assistant_icon || LOGOS.logo}
            fallback={LOGOS.logo}
            alt="Arya"
            className="header-logo-img"
          />
        </div>

        <div className="header-right">
          <span className="status-dot" style={{ background: statusColor }} title={statusLabel} />
          <span className="header-status-label">{statusLabel}</span>
          {messages.length > 0 && (
            <button className="clear-btn" onClick={clearMessages}>New chat</button>
          )}
        </div>
      </header>

      <main className="chat-main">
        <div className="messages-scroll">
          <div className="messages-container">
            {messages.length === 0 ? (
              <WelcomeScreen
                onSuggestion={(t) => {
                  setText(t);
                  textareaRef.current?.focus();
                }}
                welcomemessages={welcomemessages}
                chat_widget_icon={data_assistant_icon}
                statusLabel={statusLabel}
              />
            ) : (
              <>
                {messages.map((msg, idx) => (
                  <ChatMessage
                    key={msg.id}
                    message={msg}
                    chat_widget_icon={data_assistant_icon}
                    isLast={idx === messages.length - 1}
                    onFollowUp={(q) => { sendMessage(q); }}
                    onOpenSourceFile={handleOpenSourceFile}
                  />
                ))}
                {isTyping && <TypingIndicator chat_widget_icon={data_assistant_icon}/>}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>
        </div>

        <div className="input-area">
          <div className="input-inner">
            <div className="input-box">
              <textarea
                ref={textareaRef}
                className="chat-textarea"
                placeholder={INPUT_PLACEHOLDER}
                value={text}
                rows={1}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isTyping}
              />
              <button
                className={`send-btn ${text.trim() && !isTyping ? 'active' : ''}`}
                onClick={handleSend}
                disabled={!text.trim() || isTyping}
                title="Send (Enter)"
              >
                <SendIcon />
              </button>
            </div>
            <p className="input-hint">
              {DISCLAIMER}
            </p>
          </div>
        </div>
      </main>

      <SourcePanel
        isOpen={sourcePanel !== null}
        files={sourcePanel?.files || []}
        activeIndex={sourcePanel?.activeIndex || 0}
        onChangeFile={handleChangeSourceFile}
        onClose={handleCloseSourcePanel}
      />
    </div>
  );
}
